/**
 * Support chat API client. Mirrors backend SupportChatController (/api/v1/support/me/*).
 * Used by both the FULL and Vitrine PWAs — a shared drawer.
 */
import { http } from './http';
import { createBlobUrlCache } from '@/utils/blob';
import type { MethodFields, PaymentClientView, RefundToClientView } from './concierge';
import type { SourceKind, TopUpView } from './topup';
import type { IdentityVouchView } from './identityVouch';
import type { CardActionView } from './cardActions';
import type { MoneyRequestView } from './moneyRequests';

export type TicketStatus = 'OPEN' | 'ASSIGNED' | 'RESOLVED' | 'CLOSED';
export type Category = 'BILLING' | 'TECHNICAL' | 'TRANSFER_REQUEST' | 'OTHER';
export type Direction = 'CUSTOMER' | 'OPERATOR';

/** Mirrors `SupportRefType` in the core. `CARD_ISSUANCE` was once missing here, which is why the issuance
 *  request's thread was indistinguishable from the rest on the frontend (docs/BUGS_2026-08-16.md, B3+). */
export type RefType = 'GENERAL' | 'TOPUP_REQUEST' | 'CARD_ISSUANCE' | 'CONCIERGE' | 'CARD_CHANGE' | 'STAFF'
  /** One thread per side of a money request (docs/cabinet-redesign/07 §4.3); `refId` — the request's id. */
  | 'MONEY_REQUEST'
  /** The asked member's thread of a family vouch request (docs/cabinet-redesign/04 §0.2 З-7); `refId` — the request. */
  | 'IDENTITY_VOUCH';

export interface Ticket {
  id: string;
  status: TicketStatus;
  category: Category;
  refType: RefType | null;
  refId: string | null;
  subject: string;
  lastMessageAt: string;
  lastMessageBy: Direction;
  openedAt: string;
  firstReplyAt: string | null;
}

export interface Attachment {
  id: string;
  filename: string | null;
  contentType: string;
  byteSize: number;
}

export interface Message {
  id: string;
  direction: Direction;
  text: string;
  createdAt: string;
  readByCustomerAt: string | null;
  attachment: Attachment | null;
  /** The manager who wrote an operator bubble — public name and photo token; null for a system message (B2). */
  operator: OperatorSignature | null;
  /** TEXT, or BLOCK — a card whose content is `block` (CONCIERGE_CHAT_FIRST.md §5.2). */
  kind: 'TEXT' | 'BLOCK';
  /** The card's content; null on a TEXT message. */
  block: ChatBlock | null;
  /** The QUESTION / REQUEST_INPUT card this client message answers. */
  replyToMessageId: string | null;
}

/** The events a concierge system line names (`EventBlock.event`, §5.2). */
export type ConciergeEvent = 'RECEIVED' | 'OPENED_BY_MANAGER' | 'NEEDS_INFO' | 'REJECTED' | 'PAYMENT_CREATED'
  | 'CLIENT_MARKED_SENT' | 'PAYMENT_NEEDS_INFO' | 'FUNDS_RECEIVED' | 'ON_HOLD' | 'SENT' | 'PAYMENT_FAILED'
  | 'PAYMENT_RESET' | 'REPAYMENT_ALLOWED' | 'REFUND_PENDING' | 'REFUND_BOUNCED' | 'REFUND_DONE' | 'CLOSED'
  | 'CLOSED_STALE' | 'CANCELLED' | 'UNKNOWN';

/** Core's `ChatBlock` — the typed content of a card, a union by `type`. Concierge cards point at their object. */
export type ChatBlock =
  | { type: 'CONCIERGE_TERMS'; editionId: string }
  | { type: 'CONCIERGE_PAY_IN'; paymentId: string }
  | { type: 'CONCIERGE_EVENT'; event: ConciergeEvent | string; failure?: 'REJECTED' | 'CANCELLED' | null }
  | { type: 'CONCIERGE_REFUND_TO'; paymentId: string }
  | { type: 'REQUEST_DOCUMENT'; note: string }
  | { type: 'QUESTION'; text: string; options: string[] }
  | { type: 'REQUEST_INPUT'; label: string }
  /** A money request (docs/cabinet-redesign/07 §4.3): a pointer — what it says comes from its state. */
  | { type: 'MONEY_REQUEST'; requestId: string; side: 'PAYER' | 'REQUESTER' }
  /** Support asks this member to vouch for a family member who lost access (docs/cabinet-redesign/04 §0.2 З-7). */
  | { type: 'IDENTITY_VOUCH'; requestId: string }
  /**
   * A bank or cash top-up arranged in its thread (docs/cabinet-redesign/02 §5.1 В-3): the request, its requisites (one
   * card per release of the manager's text) and its status lines — pointers, what they say comes from their state.
   */
  | { type: 'TOPUP_REQUEST'; requestId: string }
  | { type: 'TOPUP_PAY_IN'; requestId: string; releasedAt: string }
  | { type: 'TOPUP_EVENT'; requestId: string; event: TopUpChatEvent | string }
  /**
   * Freeze / unfreeze / block through the concierge (docs/cabinet-redesign/02 §5.1 В-5): posted when the client made
   * the request and again when the manager decided — `stage` says which moment this card is.
   */
  | { type: 'CARD_ACTION'; requestId: string; stage: 'REQUESTED' | 'DONE' | 'REJECTED' };

/** A top-up status line (core `ChatBlock.TopUpEvent`); COMPLETED is the closing card with the credited amount. */
export type TopUpChatEvent = 'MARKED_SENT' | 'FUNDS_RECEIVED' | 'COMPLETED' | 'ON_HOLD' | 'EXPIRED' | 'CLOSED';

export type TermsCardState = 'CURRENT' | 'CONFIRMED' | 'SUPERSEDED' | 'CLOSED';

/**
 * The terms card's data (core `BlockState.TermsBlockView`, CONCIERGE_CHAT_FIRST.md §5.2): the edition it points to,
 * the manager's prefilled pay-in method (null without a draft) and the flags of its buttons, computed by the functions
 * the server checks with. The core sends every field; the nullable ones are null without a draft / payment.
 */
export interface TermsBlockState {
  type: 'CONCIERGE_TERMS';
  state: TermsCardState;
  recipient: string | null;
  amount: number | string | null;
  currency: string | null;
  feePercent: number | string | null;
  method: SourceKind | null;
  payInCurrency: string | null;
  network: string | null;
  methodFields: MethodFields | null;
  /** The request version for the payment body. */
  version: number;
  /** The edition id — only when it has a draft. */
  draftVersion: string | null;
  /** CURRENT and `pay()` would not refuse on its form-independent checks. */
  canConfirm: boolean;
  /** While CURRENT: the code `pay()` would refuse with (why there is no button, e.g. CONCIERGE_CARD_REQUIRED). */
  confirmRefusal: string | null;
  /** CONFIRMED and the current payment is `cancellable`. */
  canCancel: boolean;
  /** The request's current payment while CONFIRMED. */
  paymentId: string | null;
}

/** Core's `BlockState` — a card's current state (the block is a pointer, not a snapshot); event lines have none. */
export type BlockState =
  | TermsBlockState
  /**
   * `payment` — the client's view of the current payment (the same one the task answers); null when superseded.
   * `refunded` — a superseded card whose payment's funds were returned («Оплата отменена, средства возвращены»).
   */
  | { type: 'CONCIERGE_PAY_IN'; superseded: boolean; refunded: boolean; payment: PaymentClientView | null }
  /** `refundTo` — the masked refund details to confirm; null until entered or when superseded. */
  | { type: 'CONCIERGE_REFUND_TO'; superseded: boolean; refunded: boolean; refundTo: RefundToClientView | null }
  | { type: 'REQUEST_DOCUMENT'; fulfilled: boolean }
  | { type: 'QUESTION'; answered: boolean }
  | { type: 'REQUEST_INPUT'; answered: boolean }
  /** The request as this client's side sees it, computed on every poll (the same state for both sides). */
  | { type: 'MONEY_REQUEST'; request: MoneyRequestView }
  /** The family vouch request as this member sees it; null only when the request row is gone. */
  | { type: 'IDENTITY_VOUCH'; vouch: IdentityVouchView | null }
  /** The top-up as the top-up screen reads it; null only when it is gone. */
  | { type: 'TOPUP_REQUEST'; request: TopUpView | null }
  /** `superseded` — a later release replaced this card (`request` null then: «Реквизиты заменены»). */
  | { type: 'TOPUP_PAY_IN'; superseded: boolean; request: TopUpView | null }
  | { type: 'TOPUP_EVENT'; request: TopUpView | null }
  /** The card-action request as it is now; null only when it is gone. */
  | { type: 'CARD_ACTION'; request: CardActionView | null };

export interface OperatorSignature {
  name: string;
  photoToken: string | null;
}

/**
 * Who the support screen shows (B2 REQ-030..033), decided by the server: the manager who replied in this thread, the
 * one on duty, or nobody (DEFAULT — just "Personal manager"). {@code photoToken} only for a real photo.
 */
export interface ManagerView {
  mode: 'THREAD' | 'DUTY' | 'DEFAULT';
  name: string | null;
  photoToken: string | null;
}

/** The plain "Personal manager" (REQ-036) — what the screen shows before the server answered, or when it failed. */
export const DEFAULT_MANAGER: ManagerView = Object.freeze({ mode: 'DEFAULT', name: null, photoToken: null });

/** A suggested question: the FAQ's text per language — shown only in a language it has. */
export interface WelcomeSuggestion {
  id: string;
  question: Partial<Record<string, string>>;
}

export interface WelcomeView {
  manager: ManagerView;
  suggestions: WelcomeSuggestion[];
}

export interface MessagesPage {
  items: Message[];
  unreadCount: number;
  /** In every page, an empty poll included. */
  manager: ManagerView;
  /** The state of every card of the whole thread by message id, in every page (empty without cards). */
  blockStates: Record<string, BlockState>;
}

/** What the client has not read across all their threads — the source of the badge OUTSIDE the chat (B1). */
export interface ClientUnread {
  total: number;
  /** Only threads with something in them: the backend omits empty ones. */
  byTicket: { ticketId: string; unread: number }[];
}

export const supportApi = {
  myTickets() {
    return http.get<Ticket[]>('/v1/support/me/tickets').then(r => r.data);
  },
  /**
   * How many operator messages the client has not read — in total and per thread.
   *
   * A separate request is the point: the counter in the `messages()` response only appears once a thread is
   * ALREADY open, whereas a badge exists precisely so you don't have to open it (docs/BUGS_2026-08-16.md, B1).
   */
  myUnread() {
    return http.get<ClientUnread>('/v1/support/me/unread').then(r => r.data);
  },
  getTicket(id: string) {
    return http.get<Ticket>(`/v1/support/me/tickets/${id}`).then(r => r.data);
  },
  /**
   * Cursor over the (createdAt, id) composite rather than timestamp-only. The backend returns
   * `unreadCount` in the response so the FE badge needs no extra round-trip.
   */
  messages(ticketId: string, after?: { createdAt: string; id: string }) {
    return http
      .get<MessagesPage>(`/v1/support/me/tickets/${ticketId}/messages`, {
        params: after ? { afterCreatedAt: after.createdAt, afterId: after.id } : undefined,
      })
      .then(r => r.data);
  },
  /**
   * Send a message. One endpoint: pass `ticketId` to post into that specific thread (a request's own
   * chat); omit it for the general manager chat. `replyToMessageId` (with `ticketId` only) — the QUESTION /
   * REQUEST_INPUT card this answers; a card that cannot be answered is refused with 422 `INVALID_REPLY_TO`.
   */
  send(text: string, opts?: { category?: Category; ticketId?: string; replyToMessageId?: string }) {
    return http
      .post<{ ticketId: string; messageId: string }>('/v1/support/me/messages', {
        text, category: opts?.category, ticketId: opts?.ticketId, replyToMessageId: opts?.replyToMessageId,
      })
      .then(r => r.data);
  },
  /**
   * Send a file/image: raw bytes body, mime/name as params. Images + PDF only, ≤20MB (server-enforced).
   * Pass `ticketId` to attach into that specific thread; omit it for the general manager chat.
   */
  sendAttachment(file: File, ticketId?: string) {
    return http
      .post<{ ticketId: string; messageId: string }>('/v1/support/me/attachments', file, {
        headers: { 'Content-Type': 'application/octet-stream' },
        // 20 MB over mobile data does not fit the shared 30 s budget (see vitrineCard.ts).
        timeout: 300_000,
        params: { contentType: file.type || 'application/octet-stream', filename: file.name, ticketId },
      })
      .then(r => r.data);
  },
  /** The empty support screen (B2 REQ-043/044): who would answer and the suggested questions. */
  welcome() {
    return http.get<WelcomeView>('/v1/support/me/welcome').then(r => r.data);
  },
  /** Other ways to reach the manager (DVL-010), set in the backoffice; a null channel is not offered. */
  contactChannels() {
    return http.get<ContactChannels>('/v1/support/contact-channels').then(r => r.data);
  },
};

export interface ContactChannels {
  telegramUrl: string | null;
  whatsappUrl: string | null;
  email: string | null;
}

// Attachment bytes come from a JWT endpoint — <img src>/<a href> can't send Bearer, so fetch the
// blob via axios and swap in an object-URL (same pattern as faq.ts; attachments immutable by id).
// Keyed by request path (unique per ticket + attachment).
const attachmentBlobs = createBlobUrlCache(path =>
  http.get(path, { responseType: 'blob' }).then(r => r.data as Blob),
);

export function supportAttachmentUrl(ticketId: string, attachmentId: string): Promise<string> {
  return attachmentBlobs.get(`/v1/support/me/tickets/${ticketId}/attachments/${attachmentId}`);
}

/** Revokes cached object-URLs (e.g. after switching clients). */
export function clearSupportAttachmentBlobs(): void {
  attachmentBlobs.clearAll();
  operatorPhotoBlobs.clearAll();
}

// A manager's photo is keyed by its token: a new photo is a new token, so a cached one never goes stale; the
// browser's HTTP cache keeps the bytes across sessions (immutable).
const operatorPhotoBlobs = createBlobUrlCache(path =>
  http.get(path, { responseType: 'blob' }).then(r => r.data as Blob),
);

export function operatorPhotoUrl(photoToken: string): Promise<string> {
  return operatorPhotoBlobs.get(`/v1/support/operator-photos/${encodeURIComponent(photoToken)}`);
}
